import { useState } from 'react';
import {
  RotateCcw,
  RefreshCw,
  ChartNoAxesCombined,
  CalendarDays,
  Clock3,
  TriangleAlert,
  CircleAlert,
} from 'lucide-react';
import {
  AutocompleteFilter,
  type AutocompleteFilterOption,
  Button,
  DateTimePicker,
  SegmentedControl,
  Skeleton,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@shigo-ui/ui';
import { dateTimeFormat, numberFormat } from '../../../../../../lib/format';
import { dashboardT } from '../../../../../../lib/messages';
import { useShigoSession } from '../../../../../../lib/useShigoSession';
import { canReadIntegrationJourney } from '../../../../../auth/accessPolicy';
import { JourneyFlowCanvas } from './JourneyFlowCanvas';
import {
  type JourneyNodeId,
  JourneyTimeRange,
  JourneyViewMode,
  JourneyCoverageStatus,
  journeyTimeRanges,
} from './journeyFlowModel';
import {
  journeyWindowBounds,
  recentJourneyWindow,
  utcJourneyInput,
  journeyInputInstant,
  validateJourneyWindow,
} from './journeyWindow';
import { useReportFilterOptions } from '../../../../../reporting/reportExplorer/hooks';
import {
  ReportExplorerScope,
  ReportFilterField,
} from '../../../../../reporting/reportExplorer/reportEnums';
import { useJourneyQuery } from './useJourneyQuery';
import { journeyErrorMessage } from './journeyApi';
import { JOURNEY_CHART_HEIGHT, journeyNodePosition } from './journeySankeyLayout';

export function IntegrationJourneyFlow({ integrationId }: { integrationId: string }) {
  const { session } = useShigoSession();
  const [viewMode, setViewMode] = useState(JourneyViewMode.RealTime);
  const [range, setRange] = useState(JourneyTimeRange.TwentyFourHours);
  const [window, setWindow] = useState(() => recentJourneyWindow(JourneyTimeRange.TwentyFourHours));
  const [from, setFrom] = useState(() => utcJourneyInput(window.from));
  const [to, setTo] = useState(() => utcJourneyInput(window.to));
  const [validation, setValidation] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<JourneyNodeId | null>(null);
  const [publishers, setPublishers] = useState<AutocompleteFilterOption[]>([]);
  const [placements, setPlacements] = useState<AutocompleteFilterOption[]>([]);
  const [publisherSearch, setPublisherSearch] = useState('');
  const [placementSearch, setPlacementSearch] = useState('');
  const publisherOptions = useReportFilterOptions(
    ReportFilterField.Publisher,
    publisherSearch,
    Boolean(session && canReadIntegrationJourney(session.user)),
  );
  const placementOptions = useReportFilterOptions(
    ReportFilterField.Placement,
    placementSearch,
    Boolean(session && canReadIntegrationJourney(session.user)),
    ReportExplorerScope.Standard,
    undefined,
    integrationId,
  );
  const [filterError, setFilterError] = useState<string | null>(null);
  const query = useJourneyQuery({
    demandIntegrationId: integrationId,
    ...window,
    ...(publishers.length ? { publisherIds: publishers.map(({ id }) => id).sort() } : {}),
    ...(placements.length ? { placementSecondaryIds: placements.map(({ id }) => id).sort() } : {}),
  });
  const data = query.data;
  const coverageNote = query.isError ? null : data?.coverage.note;
  const selectedNode = data?.nodes.find(({ id }) => id === selectedNodeId);
  const bounds = journeyWindowBounds();
  const windowDateTimeFormat = dateTimeFormat({
    timeZone: 'UTC',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const applyWindow = (next: typeof window) => {
    setWindow(next);
    setSelectedNodeId(null);
    setValidation(null);
  };
  const refresh = () => {
    const next = viewMode === JourneyViewMode.RealTime ? recentJourneyWindow(range) : window;
    const error = validateJourneyWindow(next.from, next.to);
    setValidation(error);
    if (error) return;
    if (next.from === window.from && next.to === window.to) void query.refetch();
    else applyWindow(next);
  };
  if (!session || !canReadIntegrationJourney(session.user))
    return <p>{dashboardT('journey.denied')}</p>;

  let content;
  if (query.isPending)
    content = (
      <div role="status" aria-label={dashboardT('journey.loading')} className="overflow-hidden">
        <span className="sr-only">{dashboardT('journey.loading')}</span>
        <svg
          aria-hidden="true"
          viewBox={`0 0 1550 ${String(JOURNEY_CHART_HEIGHT)}`}
          className="text-muted h-auto w-full min-w-[1250px] motion-safe:animate-pulse sm:min-w-[980px]"
        >
          {[1, 7, 4, 4, 3].map((rows, column) => (
            <g key={column} fill="currentColor">
              <rect x={journeyNodePosition(column, 0).x} y={6} width={70} height={10} rx={4} />
              {Array.from({ length: rows }, (_, row) => {
                const node = journeyNodePosition(column, row);
                const parent = journeyNodePosition(Math.max(0, column - 1), 0);
                let height = column === 0 ? 82 : 8;
                const dominant = column === 1 && row === rows - 1;
                if (dominant) height = 64;
                const sourceY = parent.y + (dominant ? 46 : 4);
                const targetY = node.y + height / 2;
                let strokeWidth = row === 0 ? 8 : 2;
                if (dominant) strokeWidth = 64;
                const middle = (parent.x + 7 + node.x) / 2;
                return (
                  <g key={row}>
                    {column > 0 ? (
                      <path
                        d={`M${String(parent.x + 7)},${String(sourceY)}C${String(middle)},${String(sourceY)},${String(middle)},${String(targetY)},${String(node.x)},${String(targetY)}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                      />
                    ) : null}
                    <rect x={node.x} y={node.y} width={7} height={height} rx={2} />
                    <rect
                      x={node.x + 13}
                      y={node.y + height / 2 - 4}
                      width={84}
                      height={8}
                      rx={3}
                    />
                    <rect
                      x={node.x + 13}
                      y={node.y + height / 2 + 10}
                      width={48}
                      height={6}
                      rx={3}
                    />
                  </g>
                );
              })}
            </g>
          ))}
        </svg>
      </div>
    );
  else if (query.isError)
    content = (
      <div role="alert" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          style={{ aspectRatio: `1550 / ${String(JOURNEY_CHART_HEIGHT)}` }}
          className="min-w-[1250px] sm:min-w-[980px]"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 pb-7 text-center">
          <div className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full">
            <CircleAlert className="size-6" aria-hidden="true" />
          </div>
          <div className="max-w-sm space-y-1.5">
            <p className="text-sm font-medium">{dashboardT('journey.errorTitle')}</p>
            <p className="text-muted-foreground text-xs">{journeyErrorMessage(query.error)}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching}
            onClick={() => {
              void query.refetch();
            }}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            {dashboardT('journey.retry')}
          </Button>
        </div>
      </div>
    );
  else if (!data || data.coverage.status === JourneyCoverageStatus.Missing)
    content = (
      <div role="status" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          style={{ aspectRatio: `1550 / ${String(JOURNEY_CHART_HEIGHT)}` }}
          className="min-w-[1250px] sm:min-w-[980px]"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 pb-7 text-center">
          <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
            <ChartNoAxesCombined className="size-6" aria-hidden="true" />
          </div>
          <div className="max-w-sm space-y-1">
            <p className="text-sm font-medium">{dashboardT('journey.empty')}</p>
            <p className="text-muted-foreground text-xs">
              {dashboardT('journey.emptyDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  else
    content = (
      <JourneyFlowCanvas
        data={data}
        selectedNodeId={selectedNodeId}
        onSelectNode={(id) => {
          setSelectedNodeId((current) => (current === id ? null : id));
        }}
      />
    );

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <SegmentedControl
          ariaLabel={dashboardT('journey.mode')}
          value={viewMode}
          options={[
            { value: JourneyViewMode.RealTime, label: dashboardT('journey.recent') },
            { value: JourneyViewMode.CustomDate, label: dashboardT('journey.custom') },
          ]}
          onValueChange={(value) => {
            setViewMode(value);
            setValidation(null);
            if (value === JourneyViewMode.RealTime) applyWindow(recentJourneyWindow(range));
            else {
              setFrom(utcJourneyInput(window.from));
              setTo(utcJourneyInput(window.to));
            }
          }}
        />
        {viewMode === JourneyViewMode.RealTime ? (
          <div className="order-2 max-w-full min-w-0 overflow-x-auto">
            <SegmentedControl
              ariaLabel={dashboardT('journey.range')}
              value={range}
              options={journeyTimeRanges
                .filter((option) => option.minutes >= 60)
                .map((option) => ({
                  value: option.id,
                  label: option.label,
                }))}
              className="w-max"
              onValueChange={(value) => {
                setRange(value);
                applyWindow(recentJourneyWindow(value));
              }}
            />
          </div>
        ) : (
          <form
            className="order-2 flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto"
            onSubmit={(event) => {
              event.preventDefault();
              const error = validateJourneyWindow(`${from}Z`, `${to}Z`);
              setValidation(error);
              if (error) return;
              const next = {
                from: journeyInputInstant(from).toISOString(),
                to: journeyInputInstant(to).toISOString(),
              };
              if (next.from === window.from && next.to === window.to) void query.refetch();
              applyWindow(next);
            }}
          >
            <DateTimePicker
              timeZone="UTC"
              label={dashboardT('journey.from')}
              value={from}
              onChange={setFrom}
              minDateTime={utcJourneyInput(bounds.min)}
              maxDateTime={utcJourneyInput(
                Math.min(bounds.max, journeyInputInstant(to).getTime() - 60_000),
              )}
              minuteStep={1}
              className="w-full sm:w-auto"
              triggerClassName="h-9 max-w-none"
            />
            <span aria-hidden="true" className="text-muted-foreground hidden sm:inline">
              –
            </span>
            <DateTimePicker
              timeZone="UTC"
              label={dashboardT('journey.to')}
              value={to}
              onChange={setTo}
              minDateTime={utcJourneyInput(
                Math.max(bounds.min, journeyInputInstant(from).getTime() + 60_000),
              )}
              maxDateTime={utcJourneyInput(bounds.max)}
              minuteStep={1}
              className="w-full sm:w-auto"
              triggerClassName="h-9 max-w-none"
            />
            <Button type="submit" size="sm">
              {dashboardT('journey.apply')}
            </Button>
          </form>
        )}
        <Button
          variant="outline"
          size="icon"
          className="order-1 ml-auto h-9 w-9 shrink-0 sm:order-3 sm:ml-0"
          disabled={query.isFetching}
          onClick={refresh}
          aria-label={dashboardT('journey.refresh')}
          title={dashboardT('journey.refresh')}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
        <div className="order-4 grid w-full min-w-0 gap-2 sm:ml-auto sm:w-auto sm:grid-cols-2">
          {[
            {
              field: ReportFilterField.Publisher,
              selected: publishers,
              setSelected: setPublishers,
              search: publisherSearch,
              setSearch: setPublisherSearch,
              optionsQuery: publisherOptions,
            },
            {
              field: ReportFilterField.Placement,
              selected: placements,
              setSelected: setPlacements,
              search: placementSearch,
              setSearch: setPlacementSearch,
              optionsQuery: placementOptions,
            },
          ].map(({ field, selected, setSelected, search, setSearch, optionsQuery }) => {
            const options = [
              ...new Map(
                [...selected, ...(optionsQuery.data ?? [])].map((option) => [option.id, option]),
              ).values(),
            ];
            const label = dashboardT(
              field === ReportFilterField.Publisher ? 'journey.publishers' : 'journey.placements',
            );
            let emptyMessage = dashboardT('journey.searchFilters');
            if (search.trim()) emptyMessage = dashboardT('reportExplorer.filters.noValues');
            if (optionsQuery.isError)
              emptyMessage = dashboardT('reportExplorer.filters.lookupError');
            return (
              <div key={field} className="min-w-0 sm:w-64 xl:w-80">
                <AutocompleteFilter
                  label={label}
                  options={options}
                  selectedIds={selected.map(({ id }) => id)}
                  loading={optionsQuery.isFetching}
                  loadingPlaceholder={label}
                  searchPlaceholder={dashboardT('reportExplorer.filters.searchValues', {
                    field: label,
                  })}
                  emptyMessage={emptyMessage}
                  searchDebounceMs={650}
                  onSearchTermChange={setSearch}
                  onSelectedIdsChange={(ids) => {
                    if (ids.length > 100) {
                      setFilterError(dashboardT('journey.filterLimit'));
                      return;
                    }
                    setFilterError(null);
                    const selectedIds = new Set(ids);
                    setSelected(options.filter(({ id }) => selectedIds.has(id)));
                    setSelectedNodeId(null);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {filterError ? (
        <p role="alert" className="text-destructive text-sm">
          {filterError}
        </p>
      ) : null}
      {validation ? (
        <p role="alert" className="text-destructive text-sm">
          {validation}
        </p>
      ) : null}
      {query.isPending ? (
        <div
          aria-hidden="true"
          className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 py-1 pl-2"
        >
          <Skeleton className="h-4 w-64 motion-reduce:animate-none" />
          <Skeleton className="h-4 w-44 motion-reduce:animate-none" />
        </div>
      ) : null}
      {!query.isPending && data && !query.isError ? (
        <div
          className="text-muted-foreground flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 py-1 pl-2 text-xs"
          aria-live="polite"
        >
          <span className="bg-muted rounded px-1.5 font-medium">{dashboardT('journey.utc')}</span>
          <p className="flex items-center gap-1.5 tabular-nums">
            <CalendarDays className="size-3.5 shrink-0" aria-hidden="true" />
            <span>
              {windowDateTimeFormat.format(new Date(data.window.from))} –{' '}
              {windowDateTimeFormat.format(new Date(data.window.to))}
            </span>
          </p>
          {data.coverage.dataAsOf ? (
            <p className="border-border flex items-center gap-1.5 sm:border-l sm:pl-3">
              <Clock3 className="size-3.5 shrink-0" aria-hidden="true" />
              {dashboardT('journey.asOf', {
                time: windowDateTimeFormat.format(new Date(data.coverage.dataAsOf)),
              })}
              {data.coverage.dataAgeMinutes !== null && data.coverage.dataAgeMinutes > 90 ? (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-warning focus-visible:outline-ring inline-flex shrink-0 items-center rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2"
                        aria-label={dashboardT('journey.staleWarning')}
                      >
                        <TriangleAlert className="size-3.5" aria-hidden="true" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {dashboardT('journey.lastImport', {
                        minutes: numberFormat().format(data.coverage.dataAgeMinutes),
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}
      <section
        className="border-border bg-card min-w-0 rounded-md border"
        aria-labelledby="journey-title"
      >
        <header className="flex flex-wrap items-start justify-between gap-3 p-4">
          <div>
            <h2 id="journey-title" className="text-sm font-semibold">
              {dashboardT('journey.title')}
            </h2>
            <p className="text-muted-foreground mt-1 text-xs">{dashboardT('journey.help')}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={!selectedNode}
            onClick={() => {
              setSelectedNodeId(null);
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {dashboardT('journey.reset')}
          </Button>
        </header>
        {content}
        <div className="text-muted-foreground flex min-h-20 flex-col justify-end px-4 pb-4 text-xs lg:ml-[60%]">
          {query.isPending ? (
            <Skeleton aria-hidden="true" className="h-8 w-full motion-reduce:animate-none" />
          ) : (
            <p>{coverageNote}</p>
          )}
        </div>
      </section>
      <p className="sr-only" aria-live="polite">
        {selectedNode
          ? dashboardT('journey.focused', { label: selectedNode.label })
          : dashboardT('journey.all')}
      </p>
    </div>
  );
}
